
import React, { useState, useEffect } from 'react';
import { LayoutDashboard, Plus, Save, Trash2, Share2, Image as ImageIcon, Check, Loader2, Info, UploadCloud, Grid, LogOut, Scissors, Printer, Layers, Square } from 'lucide-react';
import { Button } from '../ui/Button';
import { FileUpload } from '../ui/FileUpload';
import { ShareModal } from '../ui/ShareModal';
import { ConfirmModal } from '../ui/ConfirmModal';
import { apiService } from '../../services/apiService';
import { auth } from '../../firebase';
import { MockupPosition, MockupTemplate } from '../../types';
import { TOOL_COSTS, API_BASE_URL } from '../../constants';
import { useToast } from '../ui/Toast';

interface MockupToolProps {
  onCostIncurred: (cost: number) => Promise<void>;
  checkCredits: (cost: number) => boolean;
  onResult: (result: any) => void;
  setLoading: (loading: boolean) => void;
  isLoading: boolean;
}

export const MockupTool: React.FC<MockupToolProps> = ({ 
  onCostIncurred, 
  checkCredits, 
  onResult, 
  setLoading,
  isLoading 
}) => {
  const { addToast } = useToast();
  const [activeTab, setActiveTab] = useState<'generate' | 'templates'>('generate');
  
  const [mockupImages, setMockupImages] = useState<{data: string, dims: any}[]>([]);
  const [designImage, setDesignImage] = useState<string | null>(null);
  const [skuName, setSkuName] = useState('');
  const [position, setPosition] = useState<MockupPosition>('center');
  const [designType, setDesignType] = useState<'print' | 'embroidery'>('print');
  
  // Dual Mode State
  const [placementMode, setPlacementMode] = useState<'single' | 'dual'>('single');
  const [designImageBack, setDesignImageBack] = useState<string | null>(null);
  const [positionBack, setPositionBack] = useState<MockupPosition>('back');
  
  const [templates, setTemplates] = useState<MockupTemplate[]>([]);
  const [templateNameInput, setTemplateNameInput] = useState('');
  const [selectedTemplateImage, setSelectedTemplateImage] = useState<string | null>(null);
  const [isTemplateLoading, setIsTemplateLoading] = useState(false);
  
  // Multi-select state
  const [selectedTemplateIds, setSelectedTemplateIds] = useState<Set<string>>(new Set());

  // Share state
  const [shareTemplateId, setShareTemplateId] = useState<string | null>(null);

  // Confirm Modal State
  const [confirmConfig, setConfirmConfig] = useState<{isOpen: boolean; title: string; description: string; onConfirm: () => Promise<void>; isDestructive: boolean;}>({
      isOpen: false, title: '', description: '', onConfirm: async () => {}, isDestructive: false
  });
  const [isConfirmLoading, setIsConfirmLoading] = useState(false);

  useEffect(() => {
    if (activeTab === 'templates') {
      fetchTemplates();
    }
  }, [activeTab]);

  const fetchTemplates = async () => {
    try {
      setIsTemplateLoading(true);
      const res = await apiService.getMockupTemplates();
      if (res.success) setTemplates(res.templates);
    } catch (e) {
      console.error("Failed to load templates", e);
    } finally {
      setIsTemplateLoading(false);
    }
  };

  const handleBaseImageSelect = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const result = e.target?.result as string;
      const img = new Image();
      img.onload = () => {
        setMockupImages(prev => [...prev, { 
          data: result, 
          dims: { width: img.width, height: img.height, index: prev.length } 
        }]);
      };
      img.src = result;
    };
    reader.readAsDataURL(file);
  };

  const handleDesignSelect = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
        if (e.target?.result) setDesignImage(e.target.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleDesignSelectBack = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
        if (e.target?.result) setDesignImageBack(e.target.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleSaveTemplate = async () => {
    if (!selectedTemplateImage || !templateNameInput) return;
    setLoading(true);
    try {
      const res = await apiService.saveMockupTemplate(selectedTemplateImage, templateNameInput);
      if (res.success) {
        addToast("Template saved successfully!", 'success');
        setTemplateNameInput('');
        setSelectedTemplateImage(null);
        fetchTemplates();
      }
    } catch (e: any) {
      addToast("Error saving template: " + e.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTemplate = (template: MockupTemplate, e: React.MouseEvent) => {
    e.stopPropagation();
    
    const isSharedWithMe = template.isSharedWithMe;
    
    setConfirmConfig({
        isOpen: true,
        title: isSharedWithMe ? "Remove Shared Template" : "Delete Template",
        description: isSharedWithMe 
            ? "Remove this shared template from your library?" 
            : "Permanently delete this mockup template? This action cannot be undone.",
        isDestructive: true,
        onConfirm: async () => {
            setIsConfirmLoading(true);
            try {
              if (isSharedWithMe) {
                  const myEmail = auth.currentUser?.email;
                  if (myEmail) {
                      await apiService.revokeShareMockupTemplate(template.id, myEmail);
                      addToast("Removed shared template", 'info');
                  }
              } else {
                  await apiService.deleteMockupTemplate(template.id);
                  addToast("Template deleted", 'info');
              }
              
              setTemplates(prev => prev.filter(t => t.id !== template.id));
              setSelectedTemplateIds(prev => {
                const next = new Set(prev);
                next.delete(template.id);
                return next;
              });
              setConfirmConfig(prev => ({...prev, isOpen: false}));
            } catch(e: any) { 
                console.error(e);
                addToast("Action failed: " + e.message, 'error'); 
            } finally {
                setIsConfirmLoading(false);
            }
        }
    });
  };

  const handleShareClick = (id: string, e: React.MouseEvent) => {
      e.stopPropagation();
      setShareTemplateId(id);
  };

  const handleShareConfirm = async (email: string) => {
      if (!shareTemplateId) return;
      try {
          await apiService.shareMockupTemplate(shareTemplateId, email);
          addToast("Shared successfully!", 'success');
          // Update local state
          setTemplates(prev => prev.map(t => {
              if (t.id === shareTemplateId) {
                  const newShared = t.sharedWith ? [...t.sharedWith, email] : [email];
                  return { ...t, sharedWith: newShared };
              }
              return t;
          }));
      } catch (e: any) { 
          addToast(e.message, 'error');
          throw e;
      }
  };

  const handleRevokeShare = async (email: string) => {
      if (!shareTemplateId) return;
      try {
          await apiService.revokeShareMockupTemplate(shareTemplateId, email);
          addToast(`Removed access for ${email}`, 'success');
          // Update local state
          setTemplates(prev => prev.map(t => {
              if (t.id === shareTemplateId) {
                  const newShared = t.sharedWith?.filter(e => e !== email) || [];
                  return { ...t, sharedWith: newShared };
              }
              return t;
          }));
      } catch (e: any) {
          addToast("Failed to revoke: " + e.message, 'error');
          throw e;
      }
  };

  const processTemplateImage = async (imageUrl: string): Promise<{data: string, dims: any} | null> => {
      try {
          // Use Proxy to avoid CORS
          const proxyUrl = `${API_BASE_URL}/api/proxy-image?url=${encodeURIComponent(imageUrl)}`;
          const response = await fetch(proxyUrl);
          if (!response.ok) throw new Error(`Failed to fetch image: ${response.statusText}`);
          const blob = await response.blob();
          
          return new Promise((resolve) => {
              const reader = new FileReader();
              reader.onloadend = () => {
                  const base64data = reader.result as string;
                  const img = new Image();
                  img.onload = () => {
                      resolve({
                          data: base64data,
                          dims: { width: img.width, height: img.height, index: 0 }
                      });
                  };
                  img.onerror = () => resolve(null);
                  img.src = base64data;
              };
              reader.readAsDataURL(blob);
          });
      } catch (e) {
          console.error("Image load failed", e);
          return null;
      }
  };

  const toggleTemplateSelection = (id: string) => {
      setSelectedTemplateIds(prev => {
          const next = new Set(prev);
          if (next.has(id)) next.delete(id);
          else next.add(id);
          return next;
      });
  };

  const handleUseSelectedTemplates = async () => {
      if (selectedTemplateIds.size === 0) return;
      
      setLoading(true);
      const selected = templates.filter(t => selectedTemplateIds.has(t.id));
      const newImages: {data: string, dims: any}[] = [];
      let successCount = 0;

      try {
          // Process sequentially or parallel - parallel is better for speed but check limits
          const promises = selected.map(t => processTemplateImage(t.imageUrl));
          const results = await Promise.all(promises);

          results.forEach(res => {
              if (res) {
                  // Adjust index later if needed
                  newImages.push(res);
                  successCount++;
              }
          });

          if (successCount > 0) {
              setMockupImages(prev => {
                  // Re-index new items based on existing length
                  const startIdx = prev.length;
                  const adjustedNew = newImages.map((img, i) => ({
                      ...img,
                      dims: { ...img.dims, index: startIdx + i }
                  }));
                  return [...prev, ...adjustedNew];
              });
              
              addToast(`Added ${successCount} template(s) to generator`, 'success');
              setActiveTab('generate');
              setSelectedTemplateIds(new Set()); // Clear selection
          } else {
              addToast("Failed to load selected templates.", 'error');
          }
      } catch (e: any) {
          addToast("Error processing templates: " + e.message, 'error');
      } finally {
          setLoading(false);
      }
  };

  const handleGenerate = async () => {
    if (mockupImages.length === 0 || !skuName) return;
    if (placementMode === 'single' && !designImage) return;
    if (placementMode === 'dual' && (!designImage || !designImageBack)) return;
    
    const totalCost = TOOL_COSTS.mockup * mockupImages.length;
    if (!checkCredits(totalCost)) return;

    setLoading(true);
    onResult(null);

    try {
        const result = await apiService.mockup(
            mockupImages.map(m => m.data),
            designImage!,
            mockupImages.map(m => m.dims),
            skuName,
            designType,
            position,
            placementMode,
            designImageBack,
            positionBack
        );

        if (result.success) {
            await onCostIncurred(totalCost);
            onResult(result);
            addToast("Mockups generated successfully!", 'success');
        } else {
            onResult({ success: false, error: result.error });
        }
    } catch (e: any) {
        onResult({ success: false, error: e.message });
    } finally {
        setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Share Modal */}
      <ShareModal
        isOpen={!!shareTemplateId}
        onClose={() => setShareTemplateId(null)}
        onConfirm={handleShareConfirm}
        onRevoke={handleRevokeShare}
        currentSharedWith={templates.find(t => t.id === shareTemplateId)?.sharedWith}
        title="Share Template"
        description="Share this mockup template with another team member."
      />

      <ConfirmModal 
        isOpen={confirmConfig.isOpen}
        onClose={() => setConfirmConfig(prev => ({...prev, isOpen: false}))}
        onConfirm={confirmConfig.onConfirm}
        title={confirmConfig.title}
        description={confirmConfig.description}
        isDestructive={confirmConfig.isDestructive}
        isLoading={isConfirmLoading}
        confirmText="Delete"
      />

      <div className="flex border-b border-slate-200">
        <button onClick={() => setActiveTab('generate')} className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${activeTab === 'generate' ? 'border-primary-600 text-primary-600' : 'border-transparent text-slate-500 hover:text-slate-700'}`}><LayoutDashboard size={18} /> Generator</button>
        <button onClick={() => setActiveTab('templates')} className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${activeTab === 'templates' ? 'border-primary-600 text-primary-600' : 'border-transparent text-slate-500 hover:text-slate-700'}`}><ImageIcon size={18} /> Saved Templates</button>
      </div>

      {activeTab === 'generate' ? (
        <div className="space-y-6 animate-fade-in">
           {/* Base Images */}
           <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
              <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2"><span className="w-6 h-6 rounded-full bg-slate-900 text-white flex items-center justify-center text-xs">1</span> Base Mockup Images</h3>
              <p className="text-sm text-slate-500 mb-4">Upload raw photos of plain shirts/products or select from templates. For <strong>Dual Placement</strong>, use images that show both Front and Back views.</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                  {mockupImages.map((img, idx) => (
                      <div key={idx} className="relative group bg-slate-100 rounded-lg overflow-hidden border border-slate-200 aspect-[3/4]">
                          <img src={img.data} className="w-full h-full object-cover" alt={`Base ${idx}`} />
                          <button onClick={() => setMockupImages(prev => prev.filter((_, i) => i !== idx))} className="absolute top-2 right-2 bg-red-500 text-white p-1.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"><Trash2 size={14} /></button>
                          <button onClick={() => { setSelectedTemplateImage(img.data); setTemplateNameInput(''); }} className="absolute bottom-2 right-2 bg-primary-600 text-white px-2 py-1 rounded text-xs opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1 shadow-sm"><Save size={12} /> Save Template</button>
                      </div>
                  ))}
                  <label className="border-2 border-dashed border-slate-300 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:bg-slate-50 aspect-[3/4] transition-colors">
                      <Plus size={32} className="text-slate-400 mb-2" />
                      <span className="text-xs font-semibold text-slate-500">Add Image</span>
                      <input type="file" multiple accept="image/*" className="hidden" onChange={(e) => { if(e.target.files) Array.from(e.target.files).forEach(f => handleBaseImageSelect(f)); e.target.value = ''; }} />
                  </label>
              </div>
              {selectedTemplateImage && (
                  <div className="bg-primary-50 p-4 rounded-lg flex items-center gap-3 border border-primary-100 animate-fade-in">
                      <img src={selectedTemplateImage} className="w-10 h-10 rounded object-cover border border-primary-200" />
                      <input type="text" placeholder="Template Name (e.g., White Gildan 5000)" value={templateNameInput} onChange={(e) => setTemplateNameInput(e.target.value)} className="flex-1 px-3 py-2 border border-slate-300 rounded-lg text-sm" />
                      <Button onClick={handleSaveTemplate} isLoading={isLoading} disabled={!templateNameInput} className="py-2 px-4 text-sm">Save</Button>
                      <button onClick={() => setSelectedTemplateImage(null)} className="text-slate-400 hover:text-slate-600"><Check size={20} className="rotate-45" /></button>
                  </div>
              )}
           </div>
           <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
               <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
                  <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2"><span className="w-6 h-6 rounded-full bg-slate-900 text-white flex items-center justify-center text-xs">2</span> Your Design</h3>
                  {placementMode === 'single' ? (
                      <FileUpload onFileSelect={handleDesignSelect} previewUrl={designImage} label="Upload Design File" subLabel="PNG with transparent background recommended" />
                  ) : (
                      <div className="space-y-4">
                          <div>
                              <label className="block text-sm font-medium text-slate-700 mb-1">Front Design</label>
                              <FileUpload onFileSelect={handleDesignSelect} previewUrl={designImage} label="Front Design" subLabel="For Front View" />
                          </div>
                          <div>
                              <label className="block text-sm font-medium text-slate-700 mb-1">Back Design</label>
                              <FileUpload onFileSelect={handleDesignSelectBack} previewUrl={designImageBack} label="Back Design" subLabel="For Back View" />
                          </div>
                      </div>
                  )}
               </div>
               <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm flex flex-col justify-between">
                  <div>
                      <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2"><span className="w-6 h-6 rounded-full bg-slate-900 text-white flex items-center justify-center text-xs">3</span> Settings</h3>
                      <div className="space-y-4">
                          <div>
                              <label className="block text-sm font-medium text-slate-700 mb-1">SKU / File Prefix</label>
                              <input type="text" value={skuName} onChange={(e) => setSkuName(e.target.value)} placeholder="e.g., SUMMER-TSHIRT-001" className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary-500" />
                          </div>
                          <div>
                              <label className="block text-sm font-medium text-slate-700 mb-1">Design Type</label>
                              <div className="grid grid-cols-2 gap-2">
                                  <button 
                                      className={`px-3 py-2 border rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-all ${designType === 'print' ? 'bg-primary-50 border-primary-500 text-primary-700 ring-1 ring-primary-500' : 'bg-white border-slate-300 text-slate-600 hover:bg-slate-50'}`}
                                      onClick={() => setDesignType('print')}
                                  >
                                      <Printer size={16} /> Print (DTG)
                                  </button>
                                  <button 
                                      className={`px-3 py-2 border rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-all ${designType === 'embroidery' ? 'bg-indigo-50 border-indigo-500 text-indigo-700 ring-1 ring-indigo-500' : 'bg-white border-slate-300 text-slate-600 hover:bg-slate-50'}`}
                                      onClick={() => setDesignType('embroidery')}
                                  >
                                      <Scissors size={16} /> Embroidery
                                  </button>
                              </div>
                              {designType === 'embroidery' && (
                                  <p className="text-xs text-indigo-600 mt-2 flex items-start gap-1">
                                      <Info size={14} className="mt-0.5 shrink-0" />
                                      <span>Size reduced by ~35% automatically for realistic stitching simulation.</span>
                                  </p>
                              )}
                          </div>
                          <div>
                              <label className="block text-sm font-medium text-slate-700 mb-1">Placement Mode</label>
                              <div className="grid grid-cols-2 gap-2 mb-3">
                                  <button 
                                      className={`px-3 py-2 border rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-all ${placementMode === 'single' ? 'bg-primary-50 border-primary-500 text-primary-700 ring-1 ring-primary-500' : 'bg-white border-slate-300 text-slate-600 hover:bg-slate-50'}`}
                                      onClick={() => setPlacementMode('single')}
                                  >
                                      <Square size={16} /> Single View
                                  </button>
                                  <button 
                                      className={`px-3 py-2 border rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-all ${placementMode === 'dual' ? 'bg-primary-50 border-primary-500 text-primary-700 ring-1 ring-primary-500' : 'bg-white border-slate-300 text-slate-600 hover:bg-slate-50'}`}
                                      onClick={() => setPlacementMode('dual')}
                                  >
                                      <Layers size={16} /> Front & Back
                                  </button>
                              </div>
                              
                              {placementMode === 'single' ? (
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-1">Position</label>
                                    <select value={position} onChange={(e) => setPosition(e.target.value as MockupPosition)} className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary-500">
                                        <option value="center">Center Chest</option>
                                        <option value="left-chest">Left Chest / Pocket</option>
                                        <option value="full-front">Full Front</option>
                                        <option value="back">Back Center</option>
                                    </select>
                                </div>
                              ) : (
                                <div className="space-y-3 p-3 bg-slate-50 rounded-lg border border-slate-200">
                                    <div>
                                        <label className="block text-xs font-semibold text-slate-500 mb-1">Front Placement</label>
                                        <select value={position} onChange={(e) => setPosition(e.target.value as MockupPosition)} className="w-full px-3 py-1.5 border border-slate-300 rounded-md text-sm">
                                            <option value="center">Center Chest</option>
                                            <option value="left-chest">Left Chest / Pocket</option>
                                            <option value="full-front">Full Front</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-xs font-semibold text-slate-500 mb-1">Back Placement</label>
                                        <select value={positionBack} onChange={(e) => setPositionBack(e.target.value as MockupPosition)} className="w-full px-3 py-1.5 border border-slate-300 rounded-md text-sm">
                                            <option value="center">Center Back</option>
                                            <option value="back">Upper Back</option>
                                            <option value="full-front">Full Back</option>
                                        </select>
                                    </div>
                                </div>
                              )}
                          </div>
                      </div>
                  </div>
                  <div className="mt-6 pt-6 border-t border-slate-100">
                      <Button onClick={handleGenerate} isLoading={isLoading} disabled={mockupImages.length === 0 || !designImage || !skuName || (placementMode === 'dual' && !designImageBack)} className="w-full py-3">Generate {mockupImages.length} Mockups ({TOOL_COSTS.mockup * mockupImages.length} Credits)</Button>
                  </div>
               </div>
           </div>
        </div>
      ) : (
        <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm min-h-[400px] relative">
           <div className="flex justify-between items-center mb-6">
                <div className="flex items-center gap-4">
                    <h3 className="text-lg font-bold text-slate-900">Your Template Library</h3>
                    {selectedTemplateIds.size > 0 && (
                        <span className="text-xs bg-primary-100 text-primary-700 px-2 py-1 rounded-full font-bold">
                            {selectedTemplateIds.size} Selected
                        </span>
                    )}
                </div>
                <div className="flex gap-2">
                     {selectedTemplateIds.size > 0 && (
                        <Button onClick={handleUseSelectedTemplates} isLoading={isLoading} className="py-1 px-4 text-sm h-9">
                            Use {selectedTemplateIds.size} Template{selectedTemplateIds.size > 1 ? 's' : ''}
                        </Button>
                     )}
                     <button onClick={fetchTemplates} className="text-primary-600 hover:text-primary-700 text-sm font-medium ml-2">Refresh</button>
                </div>
           </div>
           
           {isTemplateLoading ? (<div className="flex justify-center py-12"><Loader2 className="animate-spin text-slate-400" /></div>) : templates.length === 0 ? (<div className="text-center py-12 bg-slate-50 rounded-lg border border-dashed border-slate-200"><ImageIcon size={48} className="mx-auto text-slate-300 mb-2" /><p className="text-slate-500 font-medium">No templates saved yet.</p><p className="text-sm text-slate-400">Upload a base image in the Generator tab and click "Save Template".</p></div>) : (
               <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
                   {templates.map((template) => {
                       const isSelected = selectedTemplateIds.has(template.id);
                       return (
                           <div 
                                key={template.id} 
                                onClick={() => toggleTemplateSelection(template.id)}
                                className={`group relative bg-white border rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-all cursor-pointer ${isSelected ? 'ring-2 ring-primary-500 border-primary-500' : 'border-slate-200'}`}
                           >
                               <div className="aspect-[3/4] bg-slate-100 relative">
                                   <img src={template.imageUrl} className="w-full h-full object-cover" alt={template.name} />
                                   
                                   {/* Selection Checkmark */}
                                   <div className={`absolute top-2 right-2 w-5 h-5 rounded-full border border-white shadow-sm flex items-center justify-center transition-all ${isSelected ? 'bg-primary-500' : 'bg-black/30'}`}>
                                       {isSelected && <Check size={12} className="text-white" />}
                                   </div>

                                   {/* Action Overlay */}
                                   <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/60 to-transparent p-2 opacity-0 group-hover:opacity-100 transition-opacity flex justify-center gap-2" onClick={e => e.stopPropagation()}>
                                      {!template.isSharedWithMe && (
                                          <button onClick={(e) => handleShareClick(template.id, e)} className="text-white hover:text-primary-200" title="Share"><Share2 size={16} /></button>
                                      )}
                                      <button onClick={(e) => handleDeleteTemplate(template, e)} className="text-white hover:text-red-300" title={template.isSharedWithMe ? "Remove" : "Delete"}>
                                          {template.isSharedWithMe ? <LogOut size={16} /> : <Trash2 size={16} />}
                                      </button>
                                   </div>
                                   
                                   {template.isSharedWithMe && <div className="absolute top-2 left-2 bg-purple-600 text-white text-[10px] px-1.5 py-0.5 rounded shadow-sm">Shared</div>}
                               </div>
                               <div className="p-3"><h4 className="font-semibold text-sm text-slate-900 truncate" title={template.name}>{template.name}</h4><p className="text-[10px] text-slate-500">Added {new Date(template.createdAt).toLocaleDateString()}</p></div>
                           </div>
                       );
                   })}
               </div>
           )}
        </div>
      )}
    </div>
  );
};
